import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "../i18n/translations";

const STORAGE_KEY = "appLanguage";
const DEFAULT_LANGUAGE = "en";

export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English", nativeLabel: "English", code: "EN" },
  { value: "ko", label: "Korean", nativeLabel: "한국어", code: "KO" },
  { value: "ja", label: "Japanese", nativeLabel: "日本語", code: "JA" },
  { value: "zh", label: "Chinese", nativeLabel: "中文", code: "ZH" },
  { value: "fr", label: "French", nativeLabel: "Français", code: "FR" }
];

const LanguageContext = createContext(null);

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, part) => acc?.[part], obj);
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && translations[saved] ? saved : DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const setLanguage = useCallback((nextLanguage) => {
    if (!translations[nextLanguage]) return;
    setLanguageState(nextLanguage);
  }, []);

  const t = useCallback(
    (key) =>
      getNestedValue(translations[language], key) ??
      getNestedValue(translations[DEFAULT_LANGUAGE], key) ??
      key,
    [language]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, languageOptions: LANGUAGE_OPTIONS }),
    [language, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
